<template>
    <div
        class="rounded"
        :class="[
            requiresMargin ? 'mb-4' : '',
            inputDisabled ? 'cursor-not-allowed opacity-75' : '',
            !noPadding ? 'border-primary-400/10 bg-primary-500/30 border p-3' : 'inline-flex items-center',
        ]"
        @click="toggle"
    >
        <div v-if="!noPadding" class="flex justify-between gap-4" :class="hasFooter ? 'items-start' : 'items-center'">
            <div class="min-w-0 flex-1">
                <label
                    class="text-primary-200 block text-sm uppercase leading-tight"
                    :class="[small ? '' : 'tracking-wide']"
                    :for="id"
                    v-if="labelStr"
                    @click="toggle"
                >
                    <t :path="labelStr" />
                </label>

                <p class="small text-primary-300 mt-1 max-w-prose leading-relaxed" v-if="footer">
                    <t :path="footer" />
                </p>
            </div>

            <div class="switch shrink-0" :class="{ active: input, small: small }">
                <input v-model="input" :id="id" type="checkbox" :name="name" :disabled="inputDisabled" :readonly="readonly" />
                <button type="button" :class="{ [inactiveBg]: !input }" />
            </div>
        </div>

        <div v-else class="inline-flex items-center">
            <div class="switch" :class="{ active: input, small: small }">
                <input v-model="input" :id="id" type="checkbox" :name="name" :disabled="inputDisabled" :readonly="readonly" />
                <button type="button" :class="{ [inactiveBg]: !input }" />
            </div>

            <div class="ml-4 min-w-0">
                <label class="text-primary-200 block text-sm uppercase tracking-wide" :for="id" v-if="labelStr" @click="toggle">
                    <t :path="labelStr" />
                </label>

                <p class="small text-primary-300 mt-1" v-if="footer">
                    <t :path="footer" />
                </p>
            </div>
        </div>
    </div>
</template>

<style lang="css" scoped>
@reference "../../assets/css/wisp.css";

.switch {
    @apply flex cursor-pointer items-center;

    input {
        @apply hidden;
    }

    button {
        @apply relative rounded-full border-none;

        height: 18px;
        width: 36px;
        transition: 0.2s ease-in-out background-color;

        &::before {
            @apply bg-primary-50/50 absolute block rounded-full;

            content: '';
            top: -2px;
            left: -2px;
            width: 22px;
            height: 22px;
            transition:
                0.2s ease-in-out transform,
                0.2s ease-in-out background-color;
            transform: translateX(0);
        }
    }
}

.switch.small {
    button {
        height: 16px;
        width: 32px;

        &::before {
            width: 18px;
            height: 18px;
        }
    }
}

.switch.active {
    @apply shadow-none;

    button {
        @apply bg-primary-100/10;

        &::before {
            @apply bg-accent-500;
            /* travel = width - knob + 2·overhang; the small variant (32/18) lands on the same 16px */
            transform: translateX(16px);
        }
    }
}
</style>

<script lang="ts">
import { computed, defineComponent, inject, onBeforeMount, onBeforeUnmount, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
    name: 'FormSwitch',

    emits: ['update:value'],
    props: {
        name: {
            type: String,
            required: true,
        },
        value: {
            type: Boolean,
        },
        label: {
            type: [String, Array],
        },
        hideLabel: {
            type: Boolean,
        },
        noMargin: {
            type: Boolean,
        },
        noPadding: {
            type: Boolean,
            default: false,
        },
        footer: {
            type: String,
        },
        disabled: {
            type: Boolean,
        },
        readonly: {
            type: Boolean,
        },
        small: {
            type: Boolean,
            default: false,
        },
        inactiveBg: {
            type: String,
            default: 'bg-primary-600',
        },
    },

    setup(props, { emit }) {
        const { t } = useI18n();
        const input = ref(props.value || false);
        watch(
            () => props.value,
            (newValue) => {
                input.value = newValue;
            },
        );

        watch(
            () => input.value,
            (newValue) => {
                emit('update:value', newValue);
            },
        );

        let unregister: any;

        onBeforeMount(() => {
            unregister = inject<registerFormComponentFn | null>(
                'registerFormComponent',
                null,
            )?.({
                key: props.name,
                value: input,
                onSubmit: (state) => {
                    if (props.disabled) return;

                    inputDisabled.value = state;
                },
            });
        });

        onBeforeUnmount(() => {
            unregister?.();
            unregister = null;
        });

        const inputDisabled = ref(!!props.disabled); // Copy disabled as otherwise disabled fields will behave weirdly due to ref modifying props.disabled directly.

        // Watch for changes to disabled prop
        watch(
            () => props.disabled,
            (newValue) => {
                inputDisabled.value = !!newValue;
            },
        );

        const labelStr = computed(() => {
            if (props.hideLabel) return null;
            if (props.label) return props.label;

            return `components.form.fields.${props.name}`;
        });
        const hasFooter = computed(() => Boolean(props.footer?.trim()));

        const id = useId();

        return {
            t,
            input,
            inputDisabled,
            labelStr,
            hasFooter,
            id: computed(() => `${id}-${props.name}-switch`),
            requiresMargin: computed(() => {
                if (props.hideLabel || props.noMargin) {
                    return false;
                }

                return true;
            }),

            toggle: () => {
                if (inputDisabled.value) return;

                input.value = !input.value;
            },
        };
    },
});
</script>
